import { useState } from "react";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { CursorPagination } from "@/components/shared/pagination";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuditLog } from "@/hooks/use-audit-log";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

const LIMIT = 50;

const CHANGE_TYPE_OPTIONS = [
  "created",
  "renamed",
  "job_type_tag_added",
  "job_type_tag_removed",
  "subtask_added",
  "deactivated",
  "reactivated",
  "reordered",
  "bulk_imported",
];

interface AuditTrailTableProps {
  defaultEntityType?: string;
}

export function AuditTrailTable({ defaultEntityType }: AuditTrailTableProps) {
  const [entityName, setEntityName] = useState("");
  const [changeType, setChangeType] = useState("all");
  const [entityType, setEntityType] = useState(defaultEntityType ?? "all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Cursor pagination state
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([]);
  const [page, setPage] = useState(1);

  const debouncedEntityName = useDebouncedValue(entityName, 300);

  const { data, isLoading, isError, error, refetch } = useAuditLog({
    limit: LIMIT,
    cursor: cursor ?? undefined,
    entityType: entityType !== "all" ? entityType : undefined,
    entityName: debouncedEntityName || undefined,
    changeType: changeType !== "all" ? changeType : undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
  });

  function handleNext() {
    if (!data?.nextCursor) return;
    setCursorHistory((h) => [...h, cursor]);
    setCursor(data.nextCursor);
    setPage((p) => p + 1);
  }

  function handlePrev() {
    const newHistory = [...cursorHistory];
    const prevCursor = newHistory.pop() ?? null;
    setCursorHistory(newHistory);
    setCursor(prevCursor);
    setPage((p) => Math.max(1, p - 1));
  }

  function resetPagination() {
    setCursor(null);
    setCursorHistory([]);
    setPage(1);
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function formatValue(val: string | null) {
    if (!val) return <span className="text-muted-foreground">—</span>;
    try {
      return (
        <pre className="text-xs font-mono whitespace-pre-wrap max-w-xs">
          {JSON.stringify(JSON.parse(val), null, 2)}
        </pre>
      );
    } catch {
      return <span className="text-xs">{val}</span>;
    }
  }

  if (isError) {
    return (
      <ErrorState
        message={error?.message ?? "Failed to load audit log"}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Input
          placeholder="Search entity name…"
          value={entityName}
          onChange={(e) => {
            setEntityName(e.target.value);
            resetPagination();
          }}
          className="w-48"
        />
        <Select
          value={entityType}
          onValueChange={(v) => {
            setEntityType(v);
            resetPagination();
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Entity type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="category">Category</SelectItem>
            <SelectItem value="subtask">Subtask</SelectItem>
            <SelectItem value="job_type">Job Type</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={changeType}
          onValueChange={(v) => {
            setChangeType(v);
            resetPagination();
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Change type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All changes</SelectItem>
            {CHANGE_TYPE_OPTIONS.map((ct) => (
              <SelectItem key={ct} value={ct}>
                {ct.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            resetPagination();
          }}
          className="w-40"
          placeholder="From date"
        />
        <Input
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            resetPagination();
          }}
          className="w-40"
          placeholder="To date"
        />
      </div>

      {/* Table */}
      <DataTable
        headers={[
          { label: "Timestamp", className: "w-44" },
          { label: "Admin", className: "w-40" },
          { label: "Type", className: "w-28" },
          { label: "Entity", className: "w-44" },
          { label: "Change", className: "w-36" },
          { label: "Before" },
          { label: "After" },
        ]}
        loading={isLoading}
        empty={
          <EmptyState title="No audit entries" description="No log entries match your filters." />
        }
      >
        {(data?.data ?? []).map((entry) => (
          <tr
            key={entry.id}
            className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors text-sm"
          >
            <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
              {formatDate(entry.createdAt)}
            </td>
            <td className="px-4 py-2">{entry.adminName}</td>
            <td className="px-4 py-2">
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                {entry.entityType}
              </span>
            </td>
            <td className="px-4 py-2 font-medium">{entry.entityName}</td>
            <td className="px-4 py-2">
              <span className="text-xs">{entry.changeType.replace(/_/g, " ")}</span>
            </td>
            <td className="px-4 py-2 align-top">{formatValue(entry.beforeValue)}</td>
            <td className="px-4 py-2 align-top">{formatValue(entry.afterValue)}</td>
          </tr>
        ))}
      </DataTable>

      {data && (
        <CursorPagination
          total={data.total}
          page={page}
          limit={LIMIT}
          hasNext={data.hasNext}
          hasPrev={data.hasPrev}
          onNext={handleNext}
          onPrev={handlePrev}
          loading={isLoading}
          itemLabel="log entries"
        />
      )}
    </div>
  );
}
